import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  PathologyBackButton,
  PathologySubpageShell,
} from "../components/Pathology/pathologyPageChrome";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import EditNoteIcon from "@mui/icons-material/EditNote";
import MapsUgcIcon from "@mui/icons-material/MapsUgc";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import {
  getRadiologyByIdApi,
  printRadiologyApi,
  updateRadiologyApi,
} from "../services/radiology.service";
import { toast } from "react-toastify";
import AddReport from "../components/radiology/add-report";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
const ActionButtonStyle = {
  height: "44px",
  width: "44px",
  backgroundColor: "#F0F7FC",
  borderRadius: "8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};
export default function RadiologyPatientAccept() {
  const navigate = useNavigate();
  const { id } = useParams();
  const steps = [
    { label: "Pending" },
    { label: "Accepted" },
    { label: "In Progress" },
    { label: "Dispatched" },
  ];

  // Use local state for detail data
  const [detailData, setDetailData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setIsError(false);
    getRadiologyByIdApi({ id }).then(res => {
      if (res && (res.success || res.code === 200) && res.data) {
        setDetailData(res.data);
      } else {
        setDetailData(null);
        setIsError(true);
      }
      setIsLoading(false);
    }).catch(err => {
      setIsError(true);
      setDetailData(null);
      setIsLoading(false);
    });
  }, [id]);

  const [currentStep, setCurrentStep] = useState("");
  const [loading, setLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [markStatusLoading, setMarkStatusLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  /** Pending: silent no-op. Accepted: refetch from server. Other steps: silent no-op. */
  const handleMarkAsCurrentStatus = async () => {
    if (currentStep === "Pending") {
      return;
    }
    if (currentStep !== "Accepted") {
      return;
    }
    setMarkStatusLoading(true);
    try {
      const res = await getRadiologyByIdApi({ id });
      if (res && (res.success || res.code === 200) && res.data) {
        setDetailData(res.data);
        toast.success("Status and details refreshed.");
      } else {
        toast.error(res?.message || "Could not refresh this request.");
      }
    } finally {
      setMarkStatusLoading(false);
    }
  };

  const cancelReportHandler = async () => {
    let response;
    setLoading(true);
    response = await updateRadiologyApi(id, { status: "Cancelled" });
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success("Report cancelled successfully");
      // refetch detail data
      getRadiologyByIdApi({ id }).then(res => {
        setDetailData(res?.data || null);
      });
      navigate("/radiology");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const handlePrintRadiology = async () => {
    if (!id) {
      toast.error("Missing radiology request id.");
      return;
    }

    setPrintLoading(true);
    const response = await printRadiologyApi(id);
    setPrintLoading(false);

    if (
      !response ||
      response.success === false ||
      response.error ||
      response.isNetworkError
    ) {
      toast.error(response?.message || "Failed to fetch PDF");
      return;
    }

    const isBinary =
      response instanceof ArrayBuffer ||
      response instanceof Blob ||
      (typeof response === "object" &&
        response !== null &&
        typeof response.byteLength === "number");

    if (!isBinary) {
      toast.error("Invalid PDF data received");
      return;
    }

    const blob = new Blob([response], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    const tryDownload = () => {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `Radiology_Report_${id}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.info("Opened download — allow popups to print in a new tab.");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
    };

    const printWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!printWindow) {
      tryDownload();
      return;
    }

    const triggerSystemPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        /* viewer may block print() */
      }
    };
    printWindow.onload = triggerSystemPrint;
    setTimeout(triggerSystemPrint, 750);

    printWindow.addEventListener(
      "unload",
      () => {
        URL.revokeObjectURL(blobUrl);
      },
      { once: true }
    );
  };

  const submitHandler = async () => {
    let response;
    const payload =
      currentStep === "Pending"
        ? { status: "Accepted" }
        : currentStep === "Accepted"
        ? { status: "In Progress" }
        : null;

    if (!payload) {
      return;
    }

    setNextLoading(true);
    response = await updateRadiologyApi(id, payload);
    setNextLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success(`Report updated successfully`);
      // refetch detail data
      getRadiologyByIdApi({ id }).then(res => {
        setDetailData(res?.data || null);
      });
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    if (detailData) {
      setCurrentStep(detailData.status);
    }
  }, [detailData]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <ErrorMessage />
        ) : (
          <PathologySubpageShell>
            <PathologyBackButton onClick={() => navigate(-1)} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mb: "20px",
                overflow: "hidden",
              }}
            >
              {steps?.map((option, index) => (
                <Box
                  key={option.label}
                  sx={{
                    backgroundColor: "#5D6A82",
                    color: currentStep === option.label ? "#163F62" : "#5D6A82",
                    fontWeight:
                      currentStep === option.label ? "bold" : "normal",
                    textAlign: "center",
                    width: "26.2%",
                    height: "40px",
                    clipPath:
                      index === 0
                        ? "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%)"
                        : "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%, 10% 50%)",
                    display: "grid",
                    placeItems: "center",
                    mr: "-4%",
                  }}
                >
                  <Box
                    key={`${option.label}-inner`}
                    sx={{
                      transform: "scale(0.98)",
                      backgroundColor:
                        currentStep === option.label ? "#AED5F3" : "#ECF8FB",
                      height: "38px",
                      width: "100%",
                      clipPath:
                        index === 0
                          ? "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%)"
                          : "polygon(0% 0%,90% 0%,100% 50%, 90% 100%, 0% 100%, 10% 50%)",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {option.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignContent: "center",
                gap: "20px",
                paddingBottom: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                color="#5D6A82"
                sx={{ alignSelf: "center" }}
              >
                Actions:
              </Typography>
              <Box sx={ActionButtonStyle}>
                <IconButton>
                  <TaskAltIcon />
                </IconButton>
              </Box>
              <Box sx={ActionButtonStyle}>
                <IconButton>
                  <EditNoteIcon />
                </IconButton>
              </Box>
              <Box sx={ActionButtonStyle}>
                <IconButton>
                  <MapsUgcIcon />
                </IconButton>
              </Box>
              <Box sx={ActionButtonStyle}>
                <IconButton>
                  <PauseCircleOutlineIcon />
                </IconButton>
              </Box>
            </Box>

            {/* detailed report form  */}
            <Box
              sx={{
                marginBottom: "20px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                paddingInline: "15px",
              }}
            >
              <Typography
                variant="subtitle1"
                color="#5D6A82"
                paddingTop={2}
                paddingBottom={2}
              >
                Patient Information
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{ paddingLeft: 2, paddingBottom: 2 }}
              >
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="UHID "
                    value={detailData?.UHID || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Patient Name "
                    value={`${detailData?.patient?.firstName || ''} ${detailData?.patient?.lastName || ''}`}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Age "
                    value={(() => {
                      const dob = detailData?.patient?.dob;
                      if (!dob) return "-";
                      const birthDate = new Date(dob);
                      const today = new Date();
                      return Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 365.25));
                    })()}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Gender"
                    value={detailData?.patient?.gender || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                sx={{ paddingLeft: 2, paddingBottom: 2 }}
              >
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Consultation "
                    value={detailData?.consultation?.visitNo || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Doctor's Name "
                    value={`${detailData?.doctor?.firstName || ''} ${detailData?.doctor?.lastName || ''}`}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Ward Name "
                    value={detailData?.ward?.wardName || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    value={detailData?.bedNumber?.bedNumber || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    label="Bed Number "
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                marginTop: 4,
                marginBottom: "20px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                paddingInline: "15px",
              }}
            >
              <Typography
                variant="subtitle1"
                color="#5D6A82"
                paddingTop={2}
                paddingBottom={2}
              >
                Report Details
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{ paddingLeft: 2, paddingBottom: 2 }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Item Name "
                    value={detailData?.service?.itemName || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Quantity "
                    value={detailData?.quantity ?? "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Amount"
                    value={
                      detailData?.amount != null && detailData?.amount !== ""
                        ? String(detailData.amount)
                        : "-"
                    }
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    fullWidth
                    label="Status"
                    value={detailData?.status || "-"}
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ backgroundColor: "#F4F6F7" }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box
              sx={{
                display: "flex",

                justifyContent: "center",
                marginBottom: 6,
                gap: 4,
                marginTop: 6,
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  width: "200px",
                  textTransform: "none",
                  borderColor: "#0179B0",
                  color: "#0179B0",
                }}
                disabled={loading}
                onClick={cancelReportHandler}
              >
                {loading ? <CircularProgress size={20} /> : `Cancel Report`}
              </Button>
              <Button
                variant="outlined"
                sx={
                  currentStep === "Pending"
                    ? {
                        width: "250px",
                        textTransform: "none",
                        borderColor: "#92A6CC",
                        color: "#92A6CC",
                        "&:hover": {
                          borderColor: "#7a8eb3",
                          backgroundColor: "rgba(146, 166, 204, 0.06)",
                        },
                      }
                    : {
                        width: "250px",
                        textTransform: "none",
                        borderColor: "#0179B0",
                        color: "#0179B0",
                        "&:hover": {
                          borderColor: "#015a87",
                          color: "#015a87",
                          backgroundColor: "rgba(1, 121, 176, 0.06)",
                        },
                        "&.Mui-disabled": {
                          borderColor: "rgba(1, 121, 176, 0.4)",
                          color: "rgba(1, 121, 176, 0.4)",
                        },
                      }
                }
                disabled={loading || markStatusLoading}
                onClick={handleMarkAsCurrentStatus}
              >
                {markStatusLoading ? (
                  <CircularProgress
                    size={20}
                    sx={{
                      color: currentStep === "Pending" ? "#92A6CC" : "#0179B0",
                    }}
                  />
                ) : (
                  "Mark As Current Status"
                )}
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: "200px",
                  textTransform: "none",
                  backgroundColor: "#0179B0",
                }}
                onClick={
                  currentStep === "Dispatched"
                    ? handlePrintRadiology
                    : currentStep === "In Progress"
                    ? handleOpenModal
                    : submitHandler
                }
                disabled={nextLoading || printLoading}
              >
                {nextLoading ? (
                  <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                ) : currentStep === "Pending" ? (
                  "Accept"
                ) : currentStep === "Accepted" ? (
                  "Proceed"
                ) : currentStep === "In Progress" ? (
                  "Add Report"
                ) : currentStep === "Dispatched" ? (
                  printLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Print"
                  )
                ) : null}
              </Button>
            </Box>
            <AddReport
              open={modalOpen}
              onClose={handleCloseModal}
              refetch={() => {
                getRadiologyByIdApi({ id }).then(res => {
                  setDetailData(res?.data || null);
                });
              }}
              id={id}
            />
          </PathologySubpageShell>
        )}
      </Box>
    </Container>
  );
}
